import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { createLogger } from '../../utils/logger';
import { setSentryUser } from '../../config/sentry';
import { formatCEP, formatCurrency } from '../../utils/format';
import { useViaCep } from '../../hooks/useViaCep';
import {
  checkoutService,
  CheckoutApiError,
  type CheckoutResponse,
} from '../../services/checkout';
import type {
  CheckoutData,
  PersonalData,
  Address,
  CouponData,
} from '../../types';
import type { Checkout } from '../../types/checkout';

const logger = createLogger('CheckoutForm');

interface CheckoutFormProps {
  onCouponChange?: (coupon: CouponData) => void;
  onPaymentMethodChange?: (method: 'CREDIT_CARD' | 'PIX' | 'BOLETO') => void;
  data: Checkout;
  installments: {
    installment: number;
    total: number;
    perInstallment: number;
  }[];
}

export function CheckoutForm({
  // onCouponChange,
  onPaymentMethodChange,
  data,
  installments,
}: CheckoutFormProps) {
  const navigate = useNavigate();
  const { loading: cepLoading, error: cepError, searchCep } = useViaCep();

  // Estados do checkout
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const [formData, setFormData] = useState<Partial<CheckoutData>>({
    personal: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      document: '',
      birthDate: '',
      address: {
        zipCode: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
      },
    },
    payment: {
      method: 'CREDIT_CARD',
      installments: 1,
    },
    coupon: {
      code: '',
      discount: 0,
      isApplied: false,
    },
  });

  // Estados para cupom
  // const [couponCode, setCouponCode] = useState('');
  // const [couponLoading, setCouponLoading] = useState(false);
  // const [couponError, setCouponError] = useState<string | null>(null);

  const [currentStep, setCurrentStep] = useState<'personal' | 'payment'>(
    'personal'
  );

  // Filtrar opções de parcelas baseado no método de pagamento e configurações específicas
  const validInstallments = useMemo(() => {
    // Para PIX, sempre apenas 1x
    if (formData.payment?.method === 'PIX') {
      return installments.filter(option => option.installment === 1);
    }

    // Para cartão de crédito
    if (formData.payment?.method === 'CREDIT_CARD') {
      const allowInstallments =
        data?.allow_credit_card_installments || data?.allow_installments;
      if (!allowInstallments) {
        return installments.filter(option => option.installment === 1);
      }
      const maxInstallments =
        data?.max_credit_card_installments || data?.max_installments || 12;
      return installments.filter(
        option => option.installment <= maxInstallments
      );
    }

    // Para boleto
    if (formData.payment?.method === 'BOLETO') {
      const allowInstallments = data?.allow_boleto_installments;
      if (!allowInstallments) {
        return installments.filter(option => option.installment === 1);
      }
      const maxInstallments = data?.max_boleto_installments || 12;
      return installments.filter(
        option => option.installment <= maxInstallments
      );
    }

    // Fallback para compatibilidade
    return installments.filter(option => option.installment === 1);
  }, [
    installments,
    formData.payment?.method,
    data?.allow_credit_card_installments,
    data?.allow_boleto_installments,
    data?.allow_installments,
    data?.max_credit_card_installments,
    data?.max_boleto_installments,
    data?.max_installments,
  ]);

  // Validar e ajustar a seleção atual de parcelas baseado no método de pagamento
  useEffect(() => {
    const currentInstallments = formData.payment?.installments || 1;
    const paymentMethod = formData.payment?.method || 'CREDIT_CARD';

    let maxInstallments = 1;
    let allowInstallments = false;

    // Definir configurações baseadas no método de pagamento
    switch (paymentMethod) {
      case 'CREDIT_CARD':
        allowInstallments =
          data?.allow_credit_card_installments ||
          data?.allow_installments ||
          false;
        maxInstallments = allowInstallments
          ? data?.max_credit_card_installments || data?.max_installments || 12
          : 1;
        break;
      case 'BOLETO':
        allowInstallments = data?.allow_boleto_installments || false;
        maxInstallments = allowInstallments
          ? data?.max_boleto_installments || 12
          : 1;
        break;
      case 'PIX':
      default:
        allowInstallments = false;
        maxInstallments = 1;
        break;
    }

    // Se parcelamento não é permitido para o método atual, forçar para 1x
    if (!allowInstallments && currentInstallments > 1) {
      setFormData(prev => ({
        ...prev,
        payment: {
          ...prev.payment!,
          installments: 1,
        },
      }));
      return;
    }

    // Se a seleção atual excede o máximo permitido para o método, ajustar
    if (currentInstallments > maxInstallments) {
      setFormData(prev => ({
        ...prev,
        payment: {
          ...prev.payment!,
          installments: Math.min(currentInstallments, maxInstallments),
        },
      }));
    }
  }, [
    formData.payment?.method,
    data?.allow_credit_card_installments,
    data?.allow_boleto_installments,
    data?.allow_installments,
    data?.max_credit_card_installments,
    data?.max_boleto_installments,
    data?.max_installments,
    formData.payment?.installments,
  ]);

  useEffect(() => {
    if (data.payment_methods.includes('CREDIT_CARD')) {
      handlePaymentMethodChange('CREDIT_CARD');
    } else if (data.payment_methods.includes('PIX')) {
      handlePaymentMethodChange('PIX');
    } else if (data.payment_methods.includes('BOLETO')) {
      handlePaymentMethodChange('BOLETO');
    }
  }, [data.payment_methods]);

  const handlePersonalDataChange = (
    field: keyof PersonalData,
    value: string
  ) => {
    setFormData(prev => {
      const updatedPersonal = {
        ...prev.personal!,
        [field]: value,
      };

      // Atualizar dados do usuário no Sentry quando temos informações suficientes
      // Chamar após o update do estado para ter os dados mais recentes
      setTimeout(() => {
        const { firstName, lastName, email, phone } = updatedPersonal;

        // Se temos pelo menos nome ou email, atualizar o Sentry
        if (firstName || email) {
          const fullName = `${firstName || ''} ${lastName || ''}`.trim();

          setSentryUser({
            name: fullName || undefined,
            email: email || undefined,
            phone: phone || undefined,
            additionalData: {
              hasFirstName: !!firstName,
              hasLastName: !!lastName,
              formStep: 'personal-data-entry',
              checkoutId: data?.id,
            },
          });
        }
      }, 0);

      return {
        ...prev,
        personal: updatedPersonal,
      };
    });
  };

  const handleAddressChange = (field: keyof Address, value: string) => {
    setFormData(prev => ({
      ...prev,
      personal: {
        ...prev.personal!,
        address: {
          ...prev.personal!.address,
          [field]: value,
        },
      },
    }));
  };

  const handleCepChange = async (cep: string) => {
    const formattedCep = formatCEP(cep);
    handleAddressChange('zipCode', formattedCep);

    // Auto-buscar endereço quando CEP tiver 8 dígitos
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      const addressData = await searchCep(cleanCep);
      if (addressData) {
        setFormData(prev => ({
          ...prev,
          personal: {
            ...prev.personal!,
            address: {
              ...prev.personal!.address,
              ...addressData,
              zipCode: formattedCep, // Mantém formatação
            },
          },
        }));
      }
    }
  };

  const handlePaymentMethodChange = (
    method: 'CREDIT_CARD' | 'PIX' | 'BOLETO'
  ) => {
    setFormData(prev => {
      let installments = 1;

      // Determinar o número de parcelas baseado no método selecionado
      if (method === 'CREDIT_CARD') {
        const allowInstallments =
          data?.allow_credit_card_installments || data?.allow_installments;
        if (allowInstallments) {
          // Manter parcelas atuais se válido, caso contrário usar 1
          const currentInstallments = prev.payment?.installments || 1;
          const maxInstallments =
            data?.max_credit_card_installments || data?.max_installments || 12;
          installments = Math.min(currentInstallments, maxInstallments);
        }
      } else if (method === 'BOLETO') {
        const allowInstallments = data?.allow_boleto_installments;
        if (allowInstallments) {
          // Para boleto, começar com 1x por padrão
          installments = 1; // Começar sempre com 1x para boleto
        }
      }
      // Para PIX, sempre 1x

      return {
        ...prev,
        payment: {
          ...prev.payment!,
          method,
          installments,
          creditCard:
            method === 'CREDIT_CARD'
              ? prev.payment?.creditCard || {
                  number: '',
                  name: '',
                  expiryDate: '',
                  cvv: '',
                }
              : undefined,
        },
      };
    });

    // Notificar o componente pai sobre a mudança do método de pagamento
    onPaymentMethodChange?.(method);
  };

  const handleInstallmentsChange = (installments: number) => {
    const paymentMethod = formData.payment?.method || 'CREDIT_CARD';

    let maxInstallments = 1;
    let allowInstallments = false;

    // Definir limites baseados no método de pagamento
    switch (paymentMethod) {
      case 'CREDIT_CARD':
        allowInstallments =
          data?.allow_credit_card_installments ||
          data?.allow_installments ||
          false;
        maxInstallments = allowInstallments
          ? data?.max_credit_card_installments || data?.max_installments || 12
          : 1;
        break;
      case 'BOLETO':
        allowInstallments = data?.allow_boleto_installments || false;
        maxInstallments = allowInstallments
          ? data?.max_boleto_installments || 12
          : 1;
        break;
      case 'PIX':
      default:
        allowInstallments = false;
        maxInstallments = 1;
        break;
    }

    // Se parcelamento não é permitido para o método atual, forçar para 1x
    if (!allowInstallments) {
      setFormData(prev => ({
        ...prev,
        payment: {
          ...prev.payment!,
          installments: 1,
        },
      }));
      return;
    }

    // Garantir que não exceda o máximo permitido para o método
    const validInstallments = Math.min(installments, maxInstallments);

    setFormData(prev => ({
      ...prev,
      payment: {
        ...prev.payment!,
        installments: validInstallments,
      },
    }));
  };

  // const handleApplyCoupon = async () => {
  //   if (!couponCode.trim()) return;

  //   setCouponLoading(true);
  //   setCouponError(null);

  //   try {
  //     // Simulação de API para validação de cupom
  //     await new Promise(resolve => setTimeout(resolve, 1000));

  //     // Lista de cupons válidos (simulação)
  //     const validCoupons: Record<string, number> = {
  //       DESCONTO10: 10,
  //       SAVE20: 20,
  //       PROMO15: 15,
  //       BLACKFRIDAY: 25,
  //     };

  //     const discount = validCoupons[couponCode.toUpperCase()];

  //     if (discount) {
  //       setFormData(prev => ({
  //         ...prev,
  //         coupon: {
  //           code: couponCode,
  //           discount,
  //           isApplied: true,
  //         },
  //       }));
  //       setCouponError(null);

  //       // Notificar o componente pai sobre a mudança do cupom
  //       onCouponChange?.({
  //         code: couponCode,
  //         discount,
  //         isApplied: true,
  //       });
  //     } else {
  //       setCouponError('Cupom inválido ou expirado');
  //     }
  //   } catch (error) {
  //     logger.error('Erro ao aplicar cupom:', error);
  //     setCouponError('Erro ao validar cupom. Tente novamente.');
  //   } finally {
  //     setCouponLoading(false);
  //   }
  // };

  // const handleRemoveCoupon = () => {
  //   setFormData(prev => ({
  //     ...prev,
  //     coupon: {
  //       code: '',
  //       discount: 0,
  //       isApplied: false,
  //     },
  //   }));
  //   setCouponCode('');
  //   setCouponError(null);

  //   // Notificar o componente pai sobre a remoção do cupom
  //   onCouponChange?.({
  //     code: '',
  //     discount: 0,
  //     isApplied: false,
  //   });
  // };

  const handleCreditCardChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      payment: {
        ...prev.payment!,
        creditCard: {
          ...prev.payment!.creditCard!,
          [field]: value,
        },
      },
    }));
  };

  const isPersonalDataValid = () => {
    const personal = formData.personal!;
    const address = personal.address;
    return (
      personal.firstName &&
      personal.lastName &&
      personal.email &&
      personal.phone &&
      personal.document &&
      address.zipCode &&
      address.street &&
      address.number &&
      address.neighborhood &&
      address.city &&
      address.state
    );
  };

  const processCheckout = async () => {
    if (!formData.personal || !formData.payment) {
      setCheckoutError('Dados incompletos. Verifique os campos obrigatórios.');
      return;
    }

    setIsProcessing(true);
    setCheckoutError(null);

    try {
      // Atualizar dados do usuário no Sentry com informações completas antes do checkout
      const { firstName, lastName, email, phone, document } = formData.personal;
      const fullName = `${firstName || ''} ${lastName || ''}`.trim();

      setSentryUser({
        id: email || document || undefined, // Usar email ou CPF como ID único
        name: fullName,
        email: email || undefined,
        phone: phone || undefined,
        username: fullName,
        segment: 'checkout-customer',
        additionalData: {
          checkoutId: data?.id,
          productName: data?.name,
          paymentMethod: formData.payment?.method,
          installments: formData.payment?.installments,
          formStep: 'checkout-processing',
          hasAllRequiredData: true,
          document: document ? 'provided' : 'missing', // Não salvar o CPF real por segurança
        },
      });

      // Criar dados do checkout completo
      const checkoutData: CheckoutData = {
        checkout: data,
        personal: formData.personal,
        payment: formData.payment,
        checkoutId: data.id,
        coupon: formData.coupon,
      };

      const response = await checkoutService.processCheckout(checkoutData);

      logger.info('Checkout response:', response);

      if (!response.success || !response.data) {
        throw new Error(
          response.error?.message || 'Erro no processamento do checkout'
        );
      }

      // // Redirecionar baseado na resposta
      await handlePaymentRedirect(response.data);
    } catch (error) {
      logger.error('Erro no checkout:', error);

      if (error instanceof CheckoutApiError) {
        setCheckoutError(error.message);
      } else if (error instanceof Error) {
        setCheckoutError(`Erro: ${error.message}`);
      } else {
        setCheckoutError('Erro inesperado. Tente novamente em alguns minutos.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaymentRedirect = async (payment: CheckoutResponse['data']) => {
    switch (payment?.billing_type) {
      case 'CREDIT_CARD':
        navigate(`/success?payment=${payment.transaction.id}`);
        break;

      case 'PIX':
        // Para PIX, redirecionar para página de status
        navigate(`/success?payment=${payment.transaction.id}`);
        break;

      case 'BOLETO':
        // Para boleto, redirecionar para página de status
        navigate(`/success?payment=${payment.transaction.id}`);
        break;

      default:
        navigate(`/success?payment=${payment?.transaction.id}`);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (currentStep === 'personal') {
      if (isPersonalDataValid()) {
        setCurrentStep('payment');
      }
      return;
    }

    if (currentStep === 'payment') {
      if (isPaymentDataValid()) {
        processCheckout();
      }
    }
  };

  const isPaymentDataValid = () => {
    const payment = formData.payment!;

    if (payment.method === 'CREDIT_CARD') {
      return (
        payment.creditCard?.number &&
        payment.creditCard?.name &&
        payment.creditCard?.expiryDate &&
        payment.creditCard?.cvv
      );
    }

    return true;
  };

  // Formata o telefone conforme o usuário digita
  const formatPhone = (phone: string) => {
    // Remove tudo que não for número
    let digits = phone.replace(/\D/g, '');

    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 7) {
      // (XX) XXXX
      return digits.replace(/(\d{2})(\d{0,5})/, '($1) $2');
    } else if (digits.length <= 11) {
      // (XX) XXXXX-XXXX
      return digits.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
    } else {
      // Limita a 11 dígitos
      digits = digits.slice(0, 11);
      return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
  };

  const formatCpf = (cpf: string) => {
    cpf = cpf.replace(/\D/g, '');

    if (cpf.length <= 3) {
      return cpf;
    } else if (cpf.length <= 6) {
      return cpf.replace(/(\d{3})(\d+)/, '$1.$2');
    } else if (cpf.length <= 9) {
      return cpf.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
    } else {
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2}).*/, '$1.$2.$3-$4');
    }
  };

  // Formata o número do cartão de crédito conforme o usuário digita
  const formatCreditCard = (value: string) => {
    // Remove tudo que não for dígito
    let digits = value.replace(/\D/g, '');
    // Limita a 16 dígitos
    if (digits.length > 16) digits = digits.slice(0, 16);

    // Adiciona espaços a cada 4 dígitos enquanto o usuário digita
    if (digits.length > 12) {
      return digits
        .replace(/(\d{4})(\d{4})(\d{4})(\d{0,4})/, '$1 $2 $3 $4')
        .trim();
    } else if (digits.length > 8) {
      return digits.replace(/(\d{4})(\d{4})(\d{0,4})/, '$1 $2 $3').trim();
    } else if (digits.length > 4) {
      return digits.replace(/(\d{4})(\d{0,4})/, '$1 $2').trim();
    }
    return digits;
  };

  const formatCreditCardValid = (value: string) => {
    value = value.replace(/\D/g, '');

    if (value.length <= 2) {
      return value;
    } else if (value.length <= 4) {
      return value.replace(/(\d{2})(\d+)/, '$1/$2');
    }

    return value;
  };

  return (
    <div>
      {/* Erro global do checkout */}
      {checkoutError && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start">
            <svg
              className="mr-3 h-5 w-5 text-red-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-sm font-medium text-red-800">
                Erro no checkout
              </p>
              <p className="mt-1 text-sm text-red-700">{checkoutError}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Progress Steps */}
        <div className="mb-8 flex animate-fade-in-up items-center space-x-4">
          <div
            className={`flex items-center transition-all duration-300 ${currentStep === 'personal' ? 'text-blue-600' : 'text-gray-400'}`}
          >
            <div
              className={`flex h-10 w-10 transform items-center justify-center rounded-full text-sm font-bold shadow-lg transition-all duration-300 ${
                currentStep === 'personal'
                  ? 'animate-glow bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-blue-500/30'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              1
            </div>
            <span className="ml-3 text-sm font-semibold">Dados pessoais</span>
          </div>
          <div className="relative h-0.5 flex-1 overflow-hidden rounded-full bg-gradient-to-r from-gray-200 to-gray-300">
            <div
              className={`h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-700 ${currentStep === 'payment' ? 'w-full' : 'w-0'}`}
            />
          </div>
          <div
            className={`flex items-center transition-all duration-300 ${currentStep === 'payment' ? 'text-blue-600' : 'text-gray-400'}`}
          >
            <div
              className={`flex h-10 w-10 transform items-center justify-center rounded-full text-sm font-bold shadow-lg transition-all duration-300 ${
                currentStep === 'payment'
                  ? 'animate-glow bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-blue-500/30'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              2
            </div>
            <span className="ml-3 text-sm font-semibold">Pagamento</span>
          </div>
        </div>

        {/* Etapa 1: Dados Pessoais */}
        {currentStep === 'personal' && (
          <div className="animate-fade-in-up space-y-6">
            <div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Nome *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.personal?.firstName || ''}
                    onChange={e =>
                      handlePersonalDataChange('firstName', e.target.value)
                    }
                    className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Seu nome"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Sobrenome *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.personal?.lastName || ''}
                    onChange={e =>
                      handlePersonalDataChange('lastName', e.target.value)
                    }
                    className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Seu sobrenome"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  E-mail *
                </label>
                <input
                  type="email"
                  required
                  value={formData.personal?.email || ''}
                  onChange={e =>
                    handlePersonalDataChange('email', e.target.value)
                  }
                  className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="seu@email.com"
                />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Telefone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formatPhone(formData.personal?.phone || '')}
                    onChange={e =>
                      handlePersonalDataChange('phone', e.target.value)
                    }
                    className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="(11) 99999-9999"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    CPF *
                  </label>
                  <input
                    type="text"
                    required
                    value={formatCpf(formData.personal?.document || '')}
                    onChange={e =>
                      handlePersonalDataChange('document', e.target.value)
                    }
                    className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="000.000.000-00"
                  />
                </div>
              </div>

              {/* Seção de Endereço */}
              <div className="mt-6 border-t border-gray-200 pt-6">
                <div className="space-y-4">
                  {/* CEP */}
                  <div className="relative">
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      CEP *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        required
                        value={formData.personal?.address?.zipCode || ''}
                        onChange={e => handleCepChange(e.target.value)}
                        className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="00000-000"
                        maxLength={9}
                      />
                      {cepLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 transform">
                          <svg
                            className="h-5 w-5 animate-spin text-blue-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    {cepError && (
                      <p className="mt-1 animate-fade-in-up text-xs text-red-600">
                        ⚠️ {cepError}
                      </p>
                    )}
                  </div>

                  {/* Endereço e Número */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="md:col-span-2">
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Endereço *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.personal?.address?.street || ''}
                        onChange={e =>
                          handleAddressChange('street', e.target.value)
                        }
                        className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Rua, Avenida, etc."
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Número *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.personal?.address?.number || ''}
                        onChange={e =>
                          handleAddressChange('number', e.target.value)
                        }
                        className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="123"
                      />
                    </div>
                  </div>

                  {/* Complemento */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Complemento
                    </label>
                    <input
                      type="text"
                      value={formData.personal?.address?.complement || ''}
                      onChange={e =>
                        handleAddressChange('complement', e.target.value)
                      }
                      className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Apartamento, sala, etc. (opcional)"
                    />
                  </div>

                  {/* Bairro, Cidade e Estado */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Bairro *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.personal?.address?.neighborhood || ''}
                        onChange={e =>
                          handleAddressChange('neighborhood', e.target.value)
                        }
                        className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Bairro"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Cidade *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.personal?.address?.city || ''}
                        onChange={e =>
                          handleAddressChange('city', e.target.value)
                        }
                        className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Cidade"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Estado *
                      </label>
                      <select
                        required
                        value={formData.personal?.address?.state || ''}
                        onChange={e =>
                          handleAddressChange('state', e.target.value)
                        }
                        className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        title="Estado"
                      >
                        <option value="AC">Acre</option>
                        <option value="AL">Alagoas</option>
                        <option value="AP">Amapá</option>
                        <option value="AM">Amazonas</option>
                        <option value="BA">Bahia</option>
                        <option value="CE">Ceará</option>
                        <option value="DF">Distrito Federal</option>
                        <option value="ES">Espírito Santo</option>
                        <option value="GO">Goiás</option>
                        <option value="MA">Maranhão</option>
                        <option value="MT">Mato Grosso</option>
                        <option value="MS">Mato Grosso do Sul</option>
                        <option value="MG">Minas Gerais</option>
                        <option value="PA">Pará</option>
                        <option value="PB">Paraíba</option>
                        <option value="PR">Paraná</option>
                        <option value="PE">Pernambuco</option>
                        <option value="PI">Piauí</option>
                        <option value="RJ">Rio de Janeiro</option>
                        <option value="RN">Rio Grande do Norte</option>
                        <option value="RS">Rio Grande do Sul</option>
                        <option value="RO">Rondônia</option>
                        <option value="RR">Roraima</option>
                        <option value="SC">Santa Catarina</option>
                        <option value="SP">São Paulo</option>
                        <option value="SE">Sergipe</option>
                        <option value="TO">Tocantins</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={!isPersonalDataValid() || isProcessing}
                className="px-8"
              >
                Continuar
              </Button>
            </div>
          </div>
        )}

        {/* Etapa 2: Pagamento */}
        {currentStep === 'payment' && (
          <div className="animate-fade-in-up space-y-6">
            <div>
              {/* Opções de pagamento - Layout horizontal */}
              <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-3">
                {/* Cartão de crédito */}
                {data.payment_methods.includes('CREDIT_CARD') && (
                  <label
                    className="flex cursor-pointer flex-col items-center rounded-lg border-2 p-4 transition-all duration-300 hover:border-blue-300 hover:bg-blue-50 data-[selected=true]:border-blue-500 data-[selected=true]:bg-blue-50"
                    data-selected={formData.payment?.method === 'CREDIT_CARD'}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="credit"
                      checked={formData.payment?.method === 'CREDIT_CARD'}
                      onChange={() => handlePaymentMethodChange('CREDIT_CARD')}
                      className="sr-only"
                    />
                    <span className="text-center text-sm font-medium text-gray-900">
                      Cartão de crédito
                    </span>
                    <p className="mt-1 text-center text-xs text-gray-500">
                      {data?.allow_credit_card_installments ||
                      data?.allow_installments
                        ? `Até ${data?.max_credit_card_installments || data?.max_installments || 12}x${data?.credit_card_installment_fee_percentage || data?.installment_fee_percentage ? ' com juros' : ' sem juros'}`
                        : 'À vista'}
                    </p>
                  </label>
                )}

                {/* PIX */}
                {data.payment_methods.includes('PIX') && (
                  <label
                    className="flex cursor-pointer flex-col items-center rounded-lg border-2 p-4 transition-all duration-300 hover:border-blue-300 hover:bg-blue-50 data-[selected=true]:border-blue-500 data-[selected=true]:bg-blue-50"
                    data-selected={formData.payment?.method === 'PIX'}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="PIX"
                      checked={formData.payment?.method === 'PIX'}
                      onChange={() => handlePaymentMethodChange('PIX')}
                      className="sr-only"
                    />
                    <span className="text-center text-sm font-medium text-gray-900">
                      PIX
                    </span>
                    <p className="mt-1 text-center text-xs text-gray-500">
                      {data?.pix_discount_percentage
                        ? `${data.pix_discount_percentage}% de desconto`
                        : 'Pagamento instantâneo'}
                    </p>
                  </label>
                )}

                {/* Boleto */}
                {data.payment_methods.includes('BOLETO') && (
                  <label
                    className="flex cursor-pointer flex-col items-center rounded-lg border-2 p-4 transition-all duration-300 hover:border-blue-300 hover:bg-blue-50 data-[selected=true]:border-blue-500 data-[selected=true]:bg-blue-50"
                    data-selected={formData.payment?.method === 'BOLETO'}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="BOLETO"
                      checked={formData.payment?.method === 'BOLETO'}
                      onChange={() => handlePaymentMethodChange('BOLETO')}
                      className="sr-only"
                    />
                    <span className="text-center text-sm font-medium text-gray-900">
                      Boleto bancário
                    </span>
                    <p className="mt-1 text-center text-xs text-gray-500">
                      {data?.allow_boleto_installments
                        ? `Até ${data?.max_boleto_installments || 12}x${data?.boleto_installment_fee_percentage ? ' com juros' : ''}`
                        : 'Vencimento em 2 dias'}
                    </p>
                  </label>
                )}
              </div>

              {/* Cupom de desconto */}
              {/* <div className="mb-6">
                <h3 className="text-md mb-3 font-semibold text-gray-900">
                  Cupom de desconto
                </h3>
                {!formData.coupon?.isApplied ? (
                  <div className="flex space-x-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={e =>
                          setCouponCode(e.target.value.toUpperCase())
                        }
                        placeholder="Digite seu cupom"
                        className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {couponError && (
                        <p className="mt-1 animate-fade-in-up text-xs text-red-600">
                          ⚠️ {couponError}
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={handleApplyCoupon}
                      disabled={!couponCode.trim() || couponLoading}
                      className="px-6"
                    >
                      {couponLoading ? (
                        <svg
                          className="h-4 w-4 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      ) : (
                        'Aplicar'
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-4">
                    <div className="flex items-center">
                      <svg
                        className="mr-2 h-5 w-5 text-green-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-green-800">
                          Cupom {formData.coupon.code} aplicado!
                        </p>
                        <p className="text-xs text-green-600">
                          Desconto de {formData.coupon.discount}% aplicado
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveCoupon}
                      className="text-green-700 hover:text-green-900"
                    >
                      Remover
                    </Button>
                  </div>
                )}
              </div> */}

              {/* Campos do cartão de crédito */}
              {formData.payment?.method === 'CREDIT_CARD' && (
                <div className="space-y-4 rounded-lg bg-gray-50 p-4">
                  {/* Select de parcelas - exibir se cartão permitir parcelamento */}
                  {(data?.allow_credit_card_installments ||
                    data?.allow_installments) && (
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Parcelas *
                      </label>
                      <select
                        name="installments"
                        title="Parcelas"
                        value={formData.payment.installments || 1}
                        onChange={e =>
                          handleInstallmentsChange(Number(e.target.value))
                        }
                        className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {validInstallments.map(option => (
                          <option
                            key={option.installment}
                            value={option.installment}
                          >
                            {option.installment} x{' '}
                            {formatCurrency(option.perInstallment)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Número do cartão *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="0000 0000 0000 0000"
                      value={formData.payment.creditCard?.number || ''}
                      onChange={e =>
                        handleCreditCardChange(
                          'number',
                          formatCreditCard(e.target.value)
                        )
                      }
                      className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Nome no cartão *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Nome como está no cartão"
                      value={formData.payment.creditCard?.name || ''}
                      onChange={e =>
                        handleCreditCardChange('name', e.target.value)
                      }
                      className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Validade *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="MM/AA"
                        value={formData.payment.creditCard?.expiryDate || ''}
                        onChange={e =>
                          handleCreditCardChange(
                            'expiryDate',
                            formatCreditCardValid(e.target.value)
                          )
                        }
                        className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        maxLength={5}
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        CVV *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="123"
                        value={formData.payment.creditCard?.cvv || ''}
                        onChange={e =>
                          handleCreditCardChange('cvv', e.target.value)
                        }
                        className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        maxLength={3}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Aviso sobre o Pagamento PIX */}
              {formData.payment?.method === 'PIX' && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
                  <div className="mb-4 text-center">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-500">
                      <svg
                        className="h-6 w-6 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Avisos sobre o Pagamento Pix
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Atente-se aos detalhes:
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div>
                        <div className="mr-3 flex h-5 w-5 items-center justify-center rounded-full bg-green-500">
                          <svg
                            className="h-3 w-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">
                        <strong>Pix somente à vista.</strong>
                      </p>
                    </div>

                    <div className="flex items-center">
                      <div>
                        <div className="mr-3 flex h-5 w-5 items-center justify-center rounded-full bg-green-500">
                          <svg
                            className="h-3 w-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">
                        Pagamentos pelo pix é aprovado dentro alguns minutos.
                        Uma vez compensados, seu acesso será enviado por email.
                      </p>
                    </div>

                    <div className="flex items-center">
                      <div>
                        <div className="mr-3 flex h-5 w-5 items-center justify-center rounded-full bg-green-500">
                          <svg
                            className="h-3 w-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">
                        Atente-se a expiração da chave, prazo de até{' '}
                        <strong>20 minutos</strong>.
                      </p>
                    </div>

                    <div className="flex items-center">
                      <div>
                        <div className="mr-3 flex h-5 w-5 items-center justify-center rounded-full bg-green-500">
                          <svg
                            className="h-3 w-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">
                        Depois do pagamento, verifique seu e-mail para receber
                        os dados de acesso ao produto (verifique também a caixa
                        de SPAM).
                      </p>
                    </div>

                    <div className="flex items-center">
                      <div>
                        <div className="mr-3 flex h-5 w-5 items-center justify-center rounded-full bg-green-500">
                          <svg
                            className="h-3 w-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">
                        Por ordem do Banco Central (BACEN), os{' '}
                        <strong>
                          limites para compras em pix das 20hrs até as 06hrs é
                          reduzido
                        </strong>
                        . Confira seu limite antes de tentar realizar seu
                        pagamento por pix!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Informação do Boleto */}
              {formData.payment?.method === 'BOLETO' && (
                <div className="space-y-4 rounded-lg bg-gray-50 p-4">
                  {/* Select de parcelas para boleto - só exibir se permitir parcelamento */}
                  {data?.allow_boleto_installments && (
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Parcelas *
                      </label>
                      <select
                        name="installments"
                        title="Parcelas"
                        value={formData.payment.installments || 1}
                        onChange={e =>
                          handleInstallmentsChange(Number(e.target.value))
                        }
                        className="w-full rounded-xl border border-gray-300 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-gray-400 hover:shadow-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {validInstallments.map(option => (
                          <option
                            key={option.installment}
                            value={option.installment}
                          >
                            {option.installment} x{' '}
                            {formatCurrency(option.perInstallment)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Informação sobre boleto */}
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                    <div className="flex items-start">
                      <svg
                        className="mr-3 mt-0.5 h-5 w-5 text-yellow-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-yellow-800">
                          Pagamento via Boleto
                        </p>
                        <p className="mt-1 text-xs text-yellow-600">
                          {data?.allow_boleto_installments &&
                          formData.payment.installments &&
                          formData.payment.installments > 1
                            ? `Serão gerados ${formData.payment.installments} boletos. Prazo de vencimento: 2 dias úteis cada.`
                            : 'O boleto será gerado após finalizar a compra. Prazo de vencimento: 2 dias úteis.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Botões de navegação */}
            <div className="flex justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCurrentStep('personal')}
                disabled={isProcessing}
              >
                ← Voltar
              </Button>
              <Button
                type="submit"
                disabled={!isPaymentDataValid() || isProcessing}
                className="min-w-[160px]"
              >
                {isProcessing ? (
                  <div className="flex items-center">
                    <svg
                      className="mr-2 h-4 w-4 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Processando...
                  </div>
                ) : (
                  'Finalizar Compra'
                )}
              </Button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
